import differenceInDays from 'date-fns-2/differenceInDays';

import type { Intent } from '../../../../../api/Appraisal/appraisal';
import HeaderBase, { INTENT } from '../../HeaderBase';
import * as content from './OfferHeader.content';

const getHeadingCopy = ({ hasVin, isExpired, isManual, error, notDrivable }) => {
  if (error) {
    return content.heroHeadingError;
  }

  if (notDrivable) {
    return content.heroHeadingNotDrivable;
  }

  if (isExpired) {
    return content.heroHeadingExpired;
  }

  if (isManual) {
    return content.heroHeadingStumped;
  }

  return hasVin ? content.heroHeading : content.heroHeadingEstimate;
};

const getSubheadingCopy = ({
  didJustRefreshOffer,
  error,
  hasTradeIntent,
  isExpired,
  isManual,
  isVeryExpired,
  notDrivable,
  lowValue = false
}) => {
  if (error) {
    return null;
  }

  if (notDrivable) {
    return content.heroSubheadingNotDrivable;
  }

  if (isExpired) {
    return isVeryExpired ? content.heroSubheadingVeryExpired : content.heroSubheadingExpired;
  }

  if (isManual) {
    return content.heroSubheadingManual;
  }

  if (didJustRefreshOffer) {
    return content.heroSubheadingRefreshed;
  }

  if (lowValue) {
    return content.heroSubheadingLowValue;
  }

  return hasTradeIntent ? content.heroSubheadingTrade : content.heroSubheading;
};

type OfferHeaderProps = {
  didJustRefreshOffer?: boolean;
  error?: boolean;
  expiration?: string;
  hasVin?: boolean;
  intent?: Intent;
  lowValue?: boolean;
  value?: number;
  notDrivable?: boolean;
};

const OfferHeader = ({
  didJustRefreshOffer,
  error,
  expiration,
  hasVin,
  intent,
  lowValue,
  value,
  notDrivable
}: OfferHeaderProps) => {
  const expirationDate = new Date(expiration);
  const currentTime = new Date();
  const isExpired = expirationDate && expirationDate < currentTime;
  const isVeryExpired = expirationDate && differenceInDays(currentTime, expirationDate) >= 30;
  const isManual = !value;
  const hasTradeIntent = intent === INTENT.TRADE;
  const headingCopy = getHeadingCopy({ error, hasVin, isExpired, isManual, notDrivable });
  const subheadingCopy = getSubheadingCopy({
    didJustRefreshOffer,
    error,
    hasTradeIntent,
    isExpired,
    isManual,
    isVeryExpired,
    lowValue,
    notDrivable
  });

  const forceHeaderLineBreak = !isExpired && !isManual && hasVin;
  const forceSubheaderLineBreak = !isExpired && !isManual && !hasTradeIntent;

  return (
    <HeaderBase
      decorated
      forceHeaderLineBreak={forceHeaderLineBreak}
      forceSubheaderLineBreak={forceSubheaderLineBreak}
      headingCopy={headingCopy}
      lowValue={lowValue}
      subheadingCopy={subheadingCopy}
    />
  );
};

export default OfferHeader;
